// components/models/WanI2VControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import { WanI2VParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface WanI2VControlPanelProps {
  params: WanI2VParams;
  setModelParams: (params: (prevParams: { [model: string]: WanI2VParams }) => { [model: string]: WanI2VParams }) => void;
  diffusionModel: string;
}

const defaultWanI2VParams: WanI2VParams = {
  prompt: '',
  negative_prompt: '',
  duration: 5,
  resolution: '720p',
  aspect_ratio: 'auto',
  seed: -1,
};

export const getDefaultWanI2VParams = (): WanI2VParams => ({ ...defaultWanI2VParams });

const RESOLUTION_OPTIONS: NonNullable<WanI2VParams['resolution']>[] = ['480p', '580p', '720p'];
// The endpoint rejects any other ratio; 'auto' is resolved from the frame app-side.
const ASPECT_RATIO_OPTIONS: NonNullable<WanI2VParams['aspect_ratio']>[] = ['auto', '16:9', '9:16', '1:1'];

const WanI2VControlPanel: React.FC<WanI2VControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[WanI2VControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' || name === 'negative_prompt'
        ? { value_length: (newValue as string).length }
        : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'WanI2VControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[WanI2VControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'WanI2VControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt ?? ''}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={2}
          max={8}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description="In seconds."
        />
      </div>

      <div className="mb-4">
        <Select
          id="wan-2-2-i2v-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={(value) => handleSelectChange('resolution', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="wan-2-2-i2v-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio ?? 'auto'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>
    </>
  );
});

WanI2VControlPanel.displayName = 'WanI2VControlPanel';

export default WanI2VControlPanel;
